import { SyncOutlined } from '@ant-design/icons'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { Auth } from 'aws-amplify'
import { DataContext } from 'contexts/DataContextContainer'
import queryString from 'query-string'
import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import Select from 'react-select'
import axiosClient, { setAuthHeader } from 'services/axios-client'
import { InvitationContainer } from './style'

function Register() {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const [formLanguage, setFormLanguage] = useState(null)
  const [hash, setHash] = useState(null)
  const [redirect, setRedirect] = useState(null)
  const [type, setType] = useState(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { setAuthUser, setLang, setLoggedInUser } = useContext(DataContext)

  const langs = [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'Deutsch' },
  ]

  useEffect(() => {
    const queries = queryString.parse(window.location.search)
    const _type = queries['type']
    const _token = queries['token']
    const _redirect = queries['redirect']
    const lang = queries['lang']

    i18n.changeLanguage(lang)
    setLang(lang)
    localStorage.setItem('locale', lang)

    if (_token || _redirect) {
      const getInvitedUserInfo = async (hash) => {
        setLoading(true)
        const { ok, data } = await axiosClient.get(`/userByInvite/${hash}`)
        if (ok) {
          setLoading(false)
          if (!data.is_retired) {
            setFirstName(data.first_name)
            setLastName(data.last_name)
            setEmail(data.email)
          } else {
            window.location.href = '/'
          }
        }
      }

      if (_type === 'invite') {
        setHash(_token)
        setRedirect(_redirect)
        setType(_type)
        getInvitedUserInfo(_token)
      } else if (_type === 'order') {
        setHash(_token)
        setRedirect(_redirect)
        setType(_type)
        getInvitedUserInfo(_token)
      }
    }
  }, [i18n, setLang, setLoading])

  const validated = () => {
    if (!firstName || firstName.trim().length === 0) {
      setError(t('first_name_is_required'))
      return false
    }
    if (!lastName || lastName.trim().length === 0) {
      setError(t('last_name_is_required'))
      return false
    }
    const passwordRx = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,20}$/
    if (!passwordRx.test(password)) {
      setError(t('invalid_password'))
      return false
    }
    if (!confirmPassword || confirmPassword.trim().length === 0) {
      setError(t('please_confirm_password'))
      return false
    }
    if (password !== confirmPassword) {
      setError(t('password_does_not_match'))
      return false
    }
    if (!formLanguage) {
      setError(t('please_select_preferred_language'))
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (loading) {
      return
    }

    setError(null)
    if (validated()) {
      setLoading(true)

      try {
        const cognitoResponse = await Auth.signUp({
          username: email,
          password: password,
          attributes: {
            email: email,
            given_name: firstName,
            family_name: lastName,
          },
        })

        const cognitoID = cognitoResponse.userSub

        const loginUser = await Auth.signIn(email, password)
        const session = await Auth.currentSession()

        const cognitoToken = session.getAccessToken().getJwtToken()

        await axiosClient.put(`/completeInvite`, {
          hash,
          cognitoToken,
          cognitoID,
          email,
          firstName,
          lastName,
          locale: formLanguage,
        })

        const { data } = await axiosClient.post('/user/token', {
          cognitoID: loginUser.attributes.sub,
          cognitoToken,
        })
        setAuthHeader(data.token)
        setAuthUser(loginUser.attributes)

        axiosClient
          .get(`/users?cognito_id=${loginUser.attributes.sub}`)
          .then(({ data }) => {
            setLoggedInUser(data)
          })

        localStorage.setItem(
          'redirectItem',
          JSON.stringify({
            redirectUrl: null,
          }),
        )

        history.push(redirect)

        setLoading(false)
      } catch (err) {
        setLoading(false)
        setError('Unable to complete registration, please retry.')
      }
    }
  }

  const onChangeLanguage = (option) => {
    setFormLanguage(option.value)
  }

  const selectedLanguage = langs.filter((lo) => lo.value === formLanguage)[0]
  let submitButtonTitle = t('register')
  if (type === 'invite') submitButtonTitle = t('confirm_invite')

  return (
    <InvitationContainer>
      <div className="signin-wrapper">
        <h4>{t('accept_invite')}</h4>
        <h6>{t('accept_invite_narration')}</h6>
        <div>
          <div className="field">
            <input
              className="input"
              type="text"
              id="first-name"
              aria-describedby="usernameHelp"
              placeholder={t('first_name')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="field">
            <input
              className="input"
              type="text"
              id="last-name"
              aria-describedby="usernameHelp"
              placeholder={t('last_name')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="field">
            <input
              className="input"
              type="password"
              id="password"
              placeholder={t('password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <input
              className="input"
              type="password"
              id="confirm-password"
              placeholder={t('confirm_password')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <Select
              className="select-language"
              name="language"
              value={selectedLanguage}
              onChange={onChangeLanguage}
              options={langs}
              placeholder="Preferred Language / Bevorzugte Sprache"
              isSearchable={false}
            />
          </div>
          {error && <span className="error">{error}</span>}
          <div className="field">
            <button
              disabled={loading}
              className="btn btn-signin"
              onClick={handleSubmit}
            >
              {loading ? <SyncOutlined spin /> : submitButtonTitle}
            </button>
          </div>

          {window.isEnabledSSO && (
            <>
              <div className="or">
                <div className="line" />
                <span>{t('or')}</span>
              </div>
              <div className="social">
                <button
                  disabled={loading}
                  className="btn btn-facebook"
                  onClick={() => {
                    Auth.federatedSignIn({
                      provider: CognitoHostedUIIdentityProvider.Facebook,
                      customState: hash,
                    })
                  }}
                >
                  <i className="fab fa-facebook-f"></i>{' '}
                  {t('signin_with_facebook')}
                </button>
                <button
                  disabled={loading}
                  className="btn btn-google"
                  onClick={() =>
                    Auth.federatedSignIn({
                      provider: CognitoHostedUIIdentityProvider.Google,
                      customState: hash,
                    })
                  }
                >
                  <i className="fab fa-google"></i> {t('signin_with_google')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </InvitationContainer>
  )
}

export default Register
